import * as React from 'react';
import { Modal, ModalBody, ModalFooter, Button, Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';
import { ImageSelectorProps, ImageSelectorState, DIMENSION_PRESETS } from './types';
import { GalleryTab } from './tabs/gallery-tab';
import { UploadTab } from './tabs/upload-tab';
import { UrlTab } from './tabs/url-tab';
import { AiGenerateTab } from './tabs/ai-generate-tab';
import { TRexIcon } from '../../icons/t-rex';
import { loadUserImagePreferences, UserImagePreference, getDefaultImagePreference } from '../../settings-editor/ai-image-preferences/preferences';
import * as ImageConstants from '../../settings-editor/ai-image-preferences/constants';
import { RasaContext } from 'context';
import { SharedKeys, SharedStore } from '../../../shared/data-layer/sharedStore';
import { AjaxWrapper, HttpMethod } from '../../../generic/ajaxWrapper';
import { IMAGE } from '../../../shared_server_client/constants';
import './_styles.scss';

export class ImageSelectorModal extends React.Component<ImageSelectorProps, ImageSelectorState> {
  public static key: string = 'ImageSelectorModal';
  public static contextType = RasaContext;
  
  private sharedStore: SharedStore;
  private communityId: string = '';

  constructor(props: ImageSelectorProps) {
    super(props);
    
    // Determine the default active tab based on props
    let defaultTab: 'gallery' | 'upload' | 'url' | 'ai' = 'gallery';
    if (props.allowGallery !== false) defaultTab = 'gallery';
    else if (props.allowUpload !== false) defaultTab = 'upload';
    else if (props.allowURL !== false) defaultTab = 'url';
    else if (props.allowAIGeneration && props.contentType !== 'scheduled-image') defaultTab = 'ai';

    this.state = {
      activeTab: defaultTab,
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      selectedImageId: '',
      saveToGalleryChecked: props.saveToGallery || false,
      
      // Gallery tab
      imagesList: [],
      
      // URL tab
      sourceUrl: '',
      validUrl: false,
      sourceErrorMessage: '',
      
      // Upload tab
      isUploading: false,
      uploadProgress: 0,
      uploadedImageData: null,
      
      // AI tab
      isGeneratingAI: false,
      aiPrompt: this.generateInitialPrompt(),
      selectedDimensions: this.getDefaultDimensions(),
      userImagePreferences: [],
      selectedImagePreference: { key: 'custom', value: 'custom', description: 'Custom', options: null, is_default: false },
      customOptions: {
        style: ImageConstants.ImageStyleOptions[0],
        quality: ImageConstants.ImageQualityOptions[1],
        subjectFocus: ImageConstants.SubjectFocusOptions[5],
        colorScheme: ImageConstants.ColorSchemeOptions[7],
        mood: ImageConstants.MoodOptions[0],
        complexity: ImageConstants.ComplexityOptions[1],
        artStyle: ImageConstants.ArtStyleOptions[0],
        lighting: ImageConstants.LightingOptions[0],
      },
      generatedImages: [],
      selectedGeneratedImageIndex: -1
    };
    this.handleAIGenerate = this.handleAIGenerate.bind(this);
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context);
    
    // Load community info and then load preferences
    this.sharedStore.getValue(SharedKeys.activeCommunity)
      .then((activeCommunity) => {
        this.communityId = activeCommunity.communityId;
        
        // Load AI image preferences if AI generation is enabled and not scheduled image
        if (this.props.allowAIGeneration && this.props.contentType !== 'scheduled-image') {
          this.loadUserImagePreferences();
        }
        
        // Load gallery images if gallery tab is available
        if (this.props.allowGallery !== false) {
          this.loadGalleryImages();
        }
      })
      .catch(() => {
        // Fallback: still load gallery if needed
        if (this.props.allowGallery !== false) {
          this.loadGalleryImages();
        }
      });
  }

  public render() {
    const { data, size } = this.props;
    
    // Check if modal should be open based on data prop
    const isOpen = data && (typeof data === 'object' ? data.isOpen !== false : true);

    return (
      <Modal 
        isOpen={isOpen} 
        toggle={this.handleClose}
        size={size || 'lg'}
        className="image-selector-modal"
      >
        <div className="modal-header">
          <h4 className="modal-title">Select Image</h4>
          <button 
            type="button" 
            className="close" 
            onClick={this.handleClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <ModalBody className="image-selector-body">
          {this.renderTabs()}
          {this.renderTabContent()}
          {this.state.errorMessage && (
            <div className="alert alert-danger mt-3">
              {this.state.errorMessage}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="image-selector-footer">
          {this.renderFooter()}
        </ModalFooter>
      </Modal>
    );
  }

  private renderTabs = () => {
    const tabs = [];

    if (this.props.allowGallery !== false) {
      tabs.push({
        key: 'gallery',
        label: 'Library',
        icon: '📁'
      });
    }

    if (this.props.allowUpload !== false) {
      tabs.push({
        key: 'upload',
        label: 'Upload',
        icon: '📤'
      });
    }

    if (this.props.allowURL !== false) {
      tabs.push({
        key: 'url',
        label: 'URL',
        icon: '🔗'
      });
    }

    if (this.props.allowAIGeneration && this.props.contentType !== 'scheduled-image') {
      tabs.push({
        key: 'ai',
        label: 'Ask AI',
        icon: 'rex'
      });
    }

    return (
      <Nav tabs className="image-selector-tabs">
        {tabs.map(tab => (
          <NavItem key={tab.key}>
            <NavLink
              className={classnames({ active: this.state.activeTab === tab.key })}
              onClick={() => this.setActiveTab(tab.key as 'gallery' | 'upload' | 'url' | 'ai')}
            >
              <span className="tab-icon">
                {tab.icon === 'rex' ? (
                  <TRexIcon svgwidth="18" svgheight="18" />
                ) : (
                  tab.icon
                )}
              </span>
              <span className="tab-label">{tab.label}</span>
            </NavLink>
          </NavItem>
        ))}
      </Nav>
    );
  };

  private renderTabContent = () => {
    switch (this.state.activeTab) {
      case 'gallery':
        return (
          <GalleryTab
            imagesList={this.state.imagesList}
            isLoading={this.state.isLoading}
            selectedImageId={this.state.selectedImageId}
            onImageSelect={this.handleImageSelect}
            onDeleteImage={this.handleDeleteImage}
          />
        );

      case 'upload':
        return (
          <UploadTab
            isUploading={this.state.isUploading}
            uploadProgress={this.state.uploadProgress}
            uploadedImageData={this.state.uploadedImageData}
            onFileSelect={this.handleFileUpload}
            onFileDrop={this.handleFileUpload}
            onUploadAnother={this.handleUploadAnother}
          />
        );

      case 'url':
        return (
          <UrlTab
            sourceUrl={this.state.sourceUrl}
            validUrl={this.state.validUrl}
            errorMessage={this.state.sourceErrorMessage}
            isSaving={this.state.isSaving}
            onUrlChange={this.handleUrlChange}
            onUrlSubmit={this.handleUrlSubmit}
            isHostedImage={this.isHostedImage}
          />
        );

      case 'ai':
        return (
          <AiGenerateTab
            prompt={this.state.aiPrompt}
            selectedDimensions={this.state.selectedDimensions}
            isGenerating={this.state.isGeneratingAI}
            generatedImages={this.state.generatedImages}
            selectedGeneratedImageIndex={this.state.selectedGeneratedImageIndex}
            userPreferences={this.state.userImagePreferences}
            selectedPreference={this.state.selectedImagePreference}
            onPromptChange={this.handlePromptChange}
            onDimensionsChange={this.handleDimensionsChange}
            onPreferenceChange={this.handlePreferenceChange}
            onCustomOptionsChange={this.handleCustomOptionsChange}
            onGeneratedImageSelect={this.handleGeneratedImageSelect}
            onGenerate={this.handleAIGenerate}
          />
        );

      default:
        return null;
    }
  };

  private renderFooter = () => {
    const hasSelection = this.state.selectedImageId || 
                        (this.state.activeTab === 'url' && this.state.validUrl && this.state.sourceUrl) ||
                        (this.state.activeTab === 'upload' && this.state.uploadedImageData) ||
                        (this.state.activeTab === 'ai' && this.state.selectedGeneratedImageIndex >= 0);

    return (
      <div className="footer-container">
        <div className="footer-left">
          {this.state.activeTab !== 'gallery' && (
            <div className="save-to-gallery-container">
              <input
                type="checkbox"
                id="saveToGallery"
                checked={this.state.saveToGalleryChecked}
                onChange={(e) => this.setState({ saveToGalleryChecked: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="saveToGallery" className="mb-0">
                Save to Library
              </label>
            </div>
          )}
        </div>

        <div className="footer-buttons">
          <Button 
            color="secondary" 
            onClick={this.handleClose}
            disabled={this.state.isSaving || this.state.isGeneratingAI}
          >
            {this.props.closeButtonText || 'Cancel'}
          </Button>
          <Button
            color="primary"
            onClick={this.handleOk}
            disabled={!hasSelection || this.state.isSaving || this.state.isGeneratingAI}
          >
            {this.state.isSaving ? (this.state.saveToGalleryChecked ? 'Saving to Library...' : 'Saving...') : 'Select Image'}
          </Button>
        </div>
      </div>
    );
  };

  // Event handlers
  private setActiveTab = (tab: 'gallery' | 'upload' | 'url' | 'ai') => {
    this.setState({ 
      activeTab: tab,
      errorMessage: '',
      sourceErrorMessage: '',
      // Reset upload state when switching tabs
      uploadedImageData: null,
      uploadProgress: 0,
      isUploading: false,
      // Reset AI state when switching tabs
      generatedImages: [],
      selectedGeneratedImageIndex: -1,
      isGeneratingAI: false,
      // Uncheck "Save to Library" when switching to URL tab
      saveToGalleryChecked: tab === 'url' ? false : this.state.saveToGalleryChecked
    });
  };

  private handleClose = () => {
    if (this.props.closeModal) {
      this.props.closeModal(ImageSelectorModal.key);
    }
  };

  private handleImageSelect = (imageId: string) => {
    this.setState({ selectedImageId: imageId });
  };

  private handleDeleteImage = (imageId: string) => {
    // Implementation for image deletion
    // TODO: Implement image deletion logic
  };

  private handleFileUpload = (file: File) => {
    this.setState({ isUploading: true, uploadProgress: 0, uploadedImageData: null });
    
    // Show progress simulation while uploading
    const progressInterval = setInterval(() => {
      this.setState(prevState => {
        const newProgress = Math.min(prevState.uploadProgress + 15, 90); // Stop at 90% until response
        return { uploadProgress: newProgress };
      });
    }, 200);
    
    // Upload file to server using /image endpoint
    const formData = new FormData();
    formData.append(IMAGE, file);
    
    const url = `${AjaxWrapper.getServerUrl()}/${this.communityId}/image`;
    AjaxWrapper.ajax(url, HttpMethod.POST, formData, null)
      .then((hostedImage: any) => {
        clearInterval(progressInterval);
        this.setState({
          isUploading: false,
          uploadProgress: 100,
          uploadedImageData: {
            url: hostedImage.cdnUrl || hostedImage.url,
            width: hostedImage.width,
            height: hostedImage.height,
            file
          }
        });
      })
      .catch(() => {
        clearInterval(progressInterval);
        this.setState({
          isUploading: false,
          uploadProgress: 0,
          errorMessage: 'Failed to upload image. Please try again.'
        });
      });
  };

  private handleUploadAnother = () => {
    this.setState({
      uploadedImageData: null,
      uploadProgress: 0,
      isUploading: false
    });
  };

  private handleUrlChange = (url: string) => {
    this.setState({ 
      sourceUrl: url,
      validUrl: this.isValidUrl(url),
      sourceErrorMessage: ''
    });
  };

  private handleUrlSubmit = () => {
    
    if (this.state.validUrl && this.state.sourceUrl) {
      this.setState({ isSaving: true, sourceErrorMessage: '' });
      
      // Validate the image can be loaded
      const img = new Image();
      img.onload = () => {
        // Image loaded successfully
        this.setState({ isSaving: false });
        if (this.props.onSelectImage) {
          this.props.onSelectImage({
            url: this.state.sourceUrl,
            imageWidth: img.width,
            imageHeight: img.height
          });
        }
        this.handleClose();
      };
      img.onerror = () => {
        this.setState({ 
          isSaving: false,
          sourceErrorMessage: 'Unable to load image from this URL. Please check the URL and try again.'
        });
      };
      img.src = this.state.sourceUrl;
    } else {
      // URL validation failed - error state already set
    }
  };

  private handlePromptChange = (prompt: string) => {
    this.setState({ aiPrompt: prompt });
  };

  private handleDimensionsChange = (dimensions: { width: number; height: number; label: string }) => {
    this.setState({ selectedDimensions: dimensions });
  };

  private handlePreferenceChange = (preference: UserImagePreference) => {
    this.setState({ selectedImagePreference: preference });
  };

  private handleCustomOptionsChange = (customOptions: any) => {
    this.setState({ customOptions });
  };

  private handleGeneratedImageSelect = (index: number) => {
    this.setState({ selectedGeneratedImageIndex: index });
  };

  private handleAIGenerate = () => {
    this.setState({ 
      isGeneratingAI: true,
      errorMessage: '' // Clear any previous error message
    });
    
    // Use selected dimensions to determine aspect ratio
    const aspectRatio = `${this.state.selectedDimensions.width}x${this.state.selectedDimensions.height}`;
    
    // Prepare preferences payload
    let preferences = null;
    if (this.state.selectedImagePreference && this.state.selectedImagePreference.value === 'custom') {
      // Get custom options from AiGenerateTab state
      if (this.state.customOptions) {
        preferences = {
          style: this.state.customOptions.style.value,
          quality: this.state.customOptions.quality.value,
          subjectFocus: this.state.customOptions.subjectFocus.value,
          colorScheme: this.state.customOptions.colorScheme.value,
          mood: this.state.customOptions.mood.value,
          complexity: this.state.customOptions.complexity.value,
          artStyle: this.state.customOptions.artStyle.value,
          lighting: this.state.customOptions.lighting.value,
          aspectRatio,
        };
      }
    } else if (this.state.selectedImagePreference && 
               this.state.selectedImagePreference.value !== 'default' && 
               this.state.selectedImagePreference.options) {
      // Use saved preference with auto aspect ratio
      preferences = { ...this.state.selectedImagePreference.options, aspectRatio };
    } else {
      // For 'default' option or no preference, use default values from all dropdowns
      if (this.state.customOptions) {
        preferences = {
          style: this.state.customOptions.style.value,
          quality: this.state.customOptions.quality.value,
          subjectFocus: this.state.customOptions.subjectFocus.value,
          colorScheme: this.state.customOptions.colorScheme.value,
          mood: this.state.customOptions.mood.value,
          complexity: this.state.customOptions.complexity.value,
          artStyle: this.state.customOptions.artStyle.value,
          lighting: this.state.customOptions.lighting.value,
          aspectRatio,
        };
      } else {
        // Fallback to just aspectRatio if customOptions not available
        preferences = { aspectRatio };
      }
    }
    
    // Build the payload for AI generation
    const payload = {
      title: this.props.articleTitle || '',
      description: this.props.articleDescription || '',
      prompt: this.state.aiPrompt || '',
      preferences: preferences || null,
    };

    // Make the AI generation API call directly
    const url: string = `${AjaxWrapper.getServerUrl()}/ai/${this.communityId}/generate-article-image`;
    AjaxWrapper.ajax(url, HttpMethod.POST, payload)
      .then((hostedImage: any) => {
        const newImage = {
          url: hostedImage.cdnUrl || hostedImage.url,
          width: hostedImage.width,
          height: hostedImage.height,
          imageId: hostedImage.imageId,
          timestamp: Date.now()
        };
        
        this.setState(prevState => ({
          isGeneratingAI: false,
          generatedImages: [...prevState.generatedImages, newImage],
          selectedGeneratedImageIndex: prevState.generatedImages.length // Select the newly generated image
        }));
      })
      .catch(() => {
        this.setState({
          isGeneratingAI: false,
          errorMessage: 'Failed to generate image. Please try again.'
        });
      });
  };

  private handleOk = () => {
    if (this.state.selectedImageId) {
      // Handle gallery image selection
      const selectedImage = this.state.imagesList.find(img => img.imageId === this.state.selectedImageId);
      if (selectedImage) {
        if (this.props.onSelectImage) {
          this.props.onSelectImage({
            url: selectedImage.cdnUrl,
            imageWidth: selectedImage.width,
            imageHeight: selectedImage.height
          });
        }
      }
      this.handleClose();
    } else if (this.state.activeTab === 'url' && this.state.validUrl && this.state.sourceUrl) {
      // Handle URL image selection with optional save to gallery
      if (this.state.saveToGalleryChecked) {
        this.saveUrlImageToGallery(this.state.sourceUrl);
      } else {
        this.handleUrlSubmit();
      }
    } else if (this.state.activeTab === 'upload' && this.state.uploadedImageData) {
      // Handle uploaded image selection with optional save to gallery
      if (this.state.saveToGalleryChecked) {
        // Image is already uploaded to server, save by URL to gallery
        this.saveUrlImageToGallery(this.state.uploadedImageData.url);
      } else {
        if (this.props.onSelectImage) {
          this.props.onSelectImage({
            url: this.state.uploadedImageData.url,
            imageWidth: this.state.uploadedImageData.width,
            imageHeight: this.state.uploadedImageData.height
          });
        }
        this.handleClose();
      }
    } else if (this.state.activeTab === 'ai' && this.state.selectedGeneratedImageIndex >= 0) {
      // Handle AI generated image selection with optional save to gallery
      const selectedImage = this.state.generatedImages[this.state.selectedGeneratedImageIndex];
      if (selectedImage) {
        if (this.state.saveToGalleryChecked) {
          // Save generated image to gallery by URL
          this.saveUrlImageToGallery(selectedImage.url);
                  } else {
            if (this.props.onSelectImage) {
              this.props.onSelectImage({
                url: selectedImage.url,
                imageWidth: selectedImage.width,
                imageHeight: selectedImage.height
              });
            }
            this.handleClose();
        }
      }
    }
  };

  // Helper methods
  private generateInitialPrompt = (): string => {
    return '';
  };

  private getDefaultDimensions = (): { width: number; height: number; label: string } => {
    const { contentType } = this.props;
    
    if (contentType === 'scheduled-image') {
      // For scheduled images, default to Wide (landscape)
      return DIMENSION_PRESETS.find(preset => preset.label === 'Wide') || DIMENSION_PRESETS[0];
    }
    
    // For articles and events, default to Square
    return DIMENSION_PRESETS.find(preset => preset.label === 'Square') || DIMENSION_PRESETS[0];
  };


  private isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      // Check if it's http or https
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        return false;
      }
      // Basic check for image file extensions
      const pathname = urlObj.pathname.toLowerCase();
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
      const hasImageExtension = imageExtensions.some(ext => pathname.endsWith(ext));
      
      // Return true if it has image extension or if it's a valid URL (let the image load test handle validation)
      return hasImageExtension || pathname.length > 0;
    } catch {
      return false;
    }
  };

  private isHostedImage = (url: string): boolean => {
    if (!url) return false;
    const serverUrl = AjaxWrapper.getServerUrl();
    return url.includes(serverUrl) || url.includes('rasa') || url.includes(window.location.hostname);
  };

  private loadGalleryImages = () => {
    this.setState({ isLoading: true });
    
    const url: string = `${AjaxWrapper.getServerUrl()}/${this.communityId}/gallery`;
    AjaxWrapper.ajax(url, HttpMethod.GET, null)
      .then((response: any[]) => {
        this.setState({
          isLoading: false,
          imagesList: response || []
        });
      })
      .catch(() => {
        this.setState({
          isLoading: false,
          imagesList: [],
          errorMessage: 'Failed to load gallery images'
        });
      });
  };

  private loadUserImagePreferences = () => {
    loadUserImagePreferences(this.communityId)
      .then((userImagePreferences) => {
        const preferencesToShow = [...userImagePreferences];
        
        // Add Default option as first item when no default preference exists
        const hasDefault = userImagePreferences.some(pref => pref.is_default && pref.value !== 'custom');
        if (!hasDefault) {
          preferencesToShow.unshift({
            key: 'default',
            value: 'default',
            description: 'Default',
            options: null,
            is_default: true
          });
        }
        
        const defaultPreference = getDefaultImagePreference(preferencesToShow);
        
        this.setState({
          userImagePreferences: preferencesToShow,
          selectedImagePreference: defaultPreference
        });
      })
      .catch(() => {
        // Fallback to default and custom preferences if loading fails
        const fallbackPreferences = [
          { key: 'default', value: 'default', description: 'Default', options: null, is_default: true },
          { key: 'custom', value: 'custom', description: 'Custom', options: null, is_default: false }
        ];
        this.setState({
          userImagePreferences: fallbackPreferences,
          selectedImagePreference: fallbackPreferences[0]
        });
      });
  };

  private saveUrlImageToGallery = (imageUrl: string) => {
    this.setState({ isSaving: true });
    
    const url = `${AjaxWrapper.getServerUrl()}/${this.communityId}/gallery/source`;
    AjaxWrapper.ajax(url, HttpMethod.POST, { imageUrl })
      .then((hostedImage: any) => {
        // Add to gallery list
        this.setState(prevState => ({
          imagesList: [...prevState.imagesList, hostedImage],
          isSaving: false
        }));
        
        // For URL tab, always select the original URL even when saved to gallery
        if (this.state.activeTab === 'url') {
          this.handleUrlSubmit(); // This uses the original URL
        } else {
          // For other tabs (AI, upload), use the hosted image
          if (this.props.onSelectImage) {
            this.props.onSelectImage({
              url: hostedImage.cdnUrl,
              imageWidth: hostedImage.width,
              imageHeight: hostedImage.height
            });
          }
          this.handleClose();
        }
      })
      .catch(() => {
        this.setState({
          isSaving: false,
          errorMessage: 'Failed to save image to gallery'
        });
        
        // Still select the image even if save failed
        this.handleUrlSubmit();
      });
  };
}